import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'
import { useAuth, useProtectedContent } from '../../../context/AuthContext'
import callAPI from '../../../helpers/callAPI'
import useCallApi from '../../../hooks/useCallApi'
import useDateRangeFilter from '../../../hooks/useDateRangeFilter'
import useQueryParams from '../../../hooks/useQueryParams'
import Checkbox from '../../../pages/Reports/Filters/Checkbox/Checkbox'
import DateRangeFilter from '../../../pages/Reports/Filters/DateRangeFilter/DateRangeFilter'
import Affiliate from '../../../templates/Affiliate/Affiliate'
import Campaign from '../../../templates/Campaign/Campaign'
import ReportTable from '../../../templates/ReportTable/ReportTable'
import Loading from '../../../templates/UI/Loading/Loading'
import AffiliateNavbar from '../../Affiliate/Templates/AffiliateNavbar/AffiliateNavbar'

const CampaignPitchReport = ({ showSystemMessage }) => {
    const auth = useAuth()
    const showOverrideToggle = useProtectedContent(auth.permissions, 'affiliate_pitch_override')
    const { selectedStartDate, selectedEndDate, quickDate, handleChangeStart, handleChangeEnd, handleQuickDate } = useDateRangeFilter()
    const { affiliateId, campaignId, adminId, setAdminId, navigation, buildUrl } = useQueryParams()

    let apiUrl = buildUrl(`affiliate/${affiliateId}/pitches?startDate=${selectedStartDate}&endDate=${selectedEndDate}`)

    const { data, dataLoaded, refetch, setData } = useCallApi(apiUrl, 'GET', {}, [affiliateId, campaignId], showSystemMessage)

    const history = useHistory()
    const [affiliate, setAffiliate] = useState({})
    const [campaign, setCampaign] = useState({})

    const spinner = <FontAwesomeIcon icon="spinner" spin />

    useEffect(() => {
        if (data.affiliate) {
            setAffiliate(data.affiliate)
        }
        if (data.campaign) {
            setCampaign(data.campaign)
        }
    }, [data])

    const isPitchWithinNinetyDays = (pitchDate) => {
        return moment().diff(moment(pitchDate), 'days') <= 90
    }

    const handleToggleOverride = (item) => {
        const override = item.override !== 'Y'

        callAPI(`affiliate/pitches/${item.pitch_id}/override`, 'POST', { override })
            .then((result) => {
                if (result.success === true) {
                    showSystemMessage('success', result.message)
                    setData((prev) => ({
                        ...prev,
                        pitch_list: prev.pitch_list.map((pitch) => (pitch.pitch_id === item.pitch_id ? { ...pitch, override: override ? 'Y' : null } : pitch)),
                    }))
                } else if (result.success === false) {
                    showSystemMessage('error', result.errors.message)
                }
            })
            .catch((error) => {
                console.log('Error while updating pitch override: ', error)
                showSystemMessage('error', 'Connection Error')
            })
    }

    const getReportOutput = () => {
        let columns = [
            {
                Header: 'ID',
                accessor: 'offer_id',
                Cell: ({ cell: { value } }) => {
                    return <>{value}</>
                },
            },
            {
                Header: 'Campaign',
                accessor: 'name',
                Cell: (tableData) => {
                    const row = tableData.data[tableData.row.index]
                    return (
                        <span style={{ textDecoration: row.override === 'Y' ? 'line-through' : 'none' }}>
                            <Campaign name={row.name} oid={row.offer_id} status={row.status} affiliateId={affiliateId} />
                        </span>
                    )
                },
            },
            {
                Header: 'Override',
                accessor: 'override',
                Cell: (tableData) => {
                    const row = tableData.data[tableData.row.index]
                    const canEditOverride = showOverrideToggle && isPitchWithinNinetyDays(row.db_date)

                    if (canEditOverride) {
                        return (
                            <span className="border rounded px-1 d-inline-block">
                                <Checkbox id={`override-${row.pitch_id}`} name="Override" checkboxChanged={() => handleToggleOverride(row)} isChecked={row.override === 'Y'} />
                            </span>
                        )
                    }

                    return <>{row.override === 'Y' ? 'Overridden' : ''}</>
                },
            },
            {
                Header: 'Status',
                accessor: 'status',
                Cell: ({ cell: { value } }) => {
                    return <>{value}</>
                },
            },
            {
                Header: 'Date',
                accessor: 'db_date',
                Cell: ({ cell: { value } }) => {
                    return <>{moment(value).utc().format('YYYY-MM-DD HH:mm:ss')}</>
                },
            },            
        ]

        return <ReportTable data={data.pitch_list} columns={columns} />
    }

    const refreshReport = () => {
        refetch()
        let url = buildUrl(`/reports/custom/campaignPitchReport?startDate=${selectedStartDate}&endDate=${selectedEndDate}`)
        history.push(url)
    }

    return (
        <div>
            <div className="page-header d-flex align-items-center">
                <h1 className="mb-h1">Campaign Pitches</h1>
                <div className="ml-5">
                    <AffiliateNavbar campaignId={campaignId} affiliateId={affiliateId} />
                </div>
            </div>
            <div className="row">
                <div className="col-lg-8">
                    <div className="card">
                        <div className="d-flex p-3 align-items-center">
                            <h1 className="mb-h1-dark mr-2">{dataLoaded ? <Affiliate aid={affiliateId} name={affiliate.name} priority={affiliate.priority.value} status={affiliate.status.value} /> : spinner}</h1>
                            <div className="d-flex align-items-center">
                                <DateRangeFilter quickDate={quickDate} selectedStartDate={selectedStartDate} selectedEndDate={selectedEndDate} handleChangeStart={handleChangeStart} handleChangeEnd={handleChangeEnd} handleQuickDate={handleQuickDate} />
                                <Button size="sm" onClick={refreshReport}>
                                    Refresh Report
                                </Button>
                            </div>
                        </div>
                        {dataLoaded ? data.pitch_list.length === 0 ? <div className="p-3">No stats for the current parameters.</div> : getReportOutput() : <Loading />}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CampaignPitchReport
